/**
 * Central business configuration.
 *
 * Every factual detail about the firm lives here so that real values can be
 * filled in from one place. Values wrapped in [BRACKETS] are intentional
 * placeholders -- they have NOT been supplied yet and must not be guessed.
 *
 * IMPORTANT: This firm is a bookkeeping / accounting-support practice. It is
 * NOT a CPA firm. Do not change these strings in a way that implies CPA
 * licensure, audit, attest, or tax-filing services.
 */

const SITE_URL = 'https://www.calprofessionalbookkeeping.services';

export const businessInfo = {
  /** Display name derived from the client's domain. Confirm the legal entity name. */
  name: 'Cal Professional Bookkeeping',
  nameShort: 'Cal Professional',
  /** Legal entity name for the footer imprint and policy pages. */
  legalName: '[LEGAL ENTITY NAME]',

  tagline: 'Bookkeeping support for California small businesses',
  primaryMessage: 'We keep your books accurate, organized, and ready for your CPA.',

  /** --- Contact details --- */
  /**
   * Phone is stored with country code so `getPhoneHref` can build a valid
   * international tel: link. `phoneDisplay` is the human-readable form used in
   * the UI, since a bare "+1" prefix reads as noise to a local audience.
   */
  phone: '+1 (732) 910-0373',
  phoneDisplay: '1 (732) 910-0373',
  email: 'cpbk.services@gmail.com',
  /**
   * Subject pre-filled by the "email us" links, so enquiries that arrive
   * outside the website form are still recognisable and thread together.
   */
  emailInquirySubject: 'Website Inquiry',

  /** --- Coverage and availability --- */
  serviceArea: 'Sacramento, CA',
  serviceAreaLong:
    'Based in Sacramento, California, and working with small businesses throughout the state. Remote engagements are welcome.',
  businessHours: 'Mon-Fri 9AM-5PM',
  businessHoursNote: 'Appointments are scheduled in advance.',

  /** --- Domain / canonical URL used for SEO tags and structured data --- */
  siteUrl: SITE_URL,

  /** --- Professional positioning (never describe the practice as a CPA firm) --- */
  roleTitle: '[ACCOUNTING SUPERVISOR NAME]',
  roleDescription: 'Accounting Supervisor',
};

/**
 * Builds a usable `tel:` link, or returns null when the number is still a
 * placeholder. UI components check for null and render plain text instead of
 * a dead link.
 */
export const getPhoneHref = () => {
  const digits = String(businessInfo.phone).replace(/[^\d+]/g, '');
  if (!/^\+?\d{7,}$/.test(digits)) return null;
  return `tel:${digits}`;
};

/**
 * Human-readable phone number for display, or the raw value if no separate
 * display form has been supplied.
 */
export const getPhoneDisplay = () =>
  businessInfo.phoneDisplay || businessInfo.phone;

/**
 * Builds a usable `mailto:` link, or returns null while the address is a
 * placeholder.
 *
 * Pass a subject to pre-fill the visitor's mail client -- the contact page's
 * call to action and sidebar use this. Omit it for the plain address links in
 * the footer and the mobile nav.
 */
export const getEmailHref = (subject) => {
  const value = String(businessInfo.email);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return null;
  if (!subject) return `mailto:${value}`;
  return `mailto:${value}?subject=${encodeURIComponent(subject)}`;
};

export default businessInfo;
